<template>
  <div v-if="show" class="wsb" :class="`wsb--${tone}`" role="status">
    <div class="wsb-text">
      <strong class="wsb-title">{{ title }}</strong>
      <span>{{ info.message }}</span>
      <span v-if="info.held" class="wsb-held">{{ info.held }} {{ info.held === 1 ? 'email is' : 'emails are' }} held.</span>
    </div>
    <div v-if="info.can_manage && (info.can_pause || info.can_resume)" class="wsb-actions">
      <button v-if="info.can_resume" type="button" class="btn btn-sm btn-primary" :disabled="busy" @click="act('resume')">Resume sending</button>
    </div>
    <div v-if="error" class="wsb-error">{{ error }}</div>
  </div>
  <div v-else-if="info && info.state === 'live' && info.can_manage && !info.kill_switch" class="wsb-live">
    <button type="button" class="btn btn-link btn-sm p-0" :disabled="busy" @click="act('pause')">Pause all sending</button>
    <span v-if="error" class="wsb-error">{{ error }}</span>
  </div>
</template>

<script>
/**
 * WorkspaceSendingBanner - tells everyone in a workspace when its email
 * sending is not on (send safety slice 2), and lets the workspace owner
 * pause live sending or resume their own pause. Reads
 * GET /api/v1/admin/sending/state (Ops::OUTREACH); the pause and resume
 * buttons only appear when the server says this user may use them
 * (can_manage), because a refused request would send the app to the login
 * page. Nothing is fetched without a session. When Yorlin paused sending by
 * itself (send safety slice 9) the server's message says why, with the
 * numbers, and that only Yorlin can lift it; no resume button is offered.
 */
import { useRoute } from "vue-router";
import useApi from "@apexl/icarus-core/src/services/api";
import useTokenStore from "@apexl/icarus-core/src/store/useTokenStore";

const ENDPOINT = '/api/v1/admin/sending';

export default {
  name: 'WorkspaceSendingBanner',
  data() {
    return { api: useApi(), route: useRoute(), info: null, busy: false, error: '' };
  },
  computed: {
    show() {
      return !!this.info && (this.info.state !== 'live' || this.info.kill_switch);
    },
    tone() {
      if (this.info?.kill_switch || this.info?.auto_pause) return 'danger';
      return { test: 'warning', paused: 'neutral', off: 'danger' }[this.info?.state] || 'neutral';
    },
    title() {
      if (this.info?.kill_switch) return 'Sending paused';
      if (this.info?.auto_pause) return 'Sending paused automatically';
      return { off: 'Sending is off', test: 'Test mode', paused: 'Sending paused' }[this.info?.state] || '';
    },
  },
  watch: {
    'route.path'() { this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      const { accessToken } = useTokenStore();
      if (!accessToken) {
        this.info = null;
        return;
      }
      try {
        const p = await this.api.get(`${ENDPOINT}/state`);
        this.info = p?.data || null;
      } catch (e) {
        this.info = null;
      }
    },
    async act(what) {
      if (what === 'pause' && !window.confirm('Pause all email sending for this workspace? Campaigns, drips and one-off emails will be held until you resume.')) return;
      this.busy = true; this.error = '';
      try {
        const p = await this.api.post(`${ENDPOINT}/${what}`, {});
        if (p?.error) { this.error = p.error; }
        if (p?.data) { this.info = p.data; }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'That did not work. Please try again.';
      }
      this.busy = false;
    },
  },
};
</script>

<style scoped>
.wsb { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 10px 16px; margin: 0 0 12px; border-radius: 8px; font-size: 0.85rem; border: 1px solid transparent; }
.wsb--danger { background: #fef3f2; border-color: #fecdca; color: #7a271a; }
.wsb--warning { background: #fffaeb; border-color: #fedf89; color: #7a2e0e; }
.wsb--neutral { background: #f2f4f7; border-color: #d0d5dd; color: #344054; }
.wsb-text { flex: 1 1 320px; display: flex; gap: 6px; flex-wrap: wrap; }
.wsb-title { font-weight: 700; }
.wsb-held { font-weight: 600; }
.wsb-actions { display: flex; gap: 8px; }
.wsb-error { flex-basis: 100%; font-size: 0.8rem; color: #b42318; }
.wsb-live { display: flex; justify-content: flex-end; gap: 8px; margin: 0 0 8px; font-size: 0.8rem; }
</style>
